import { storyblokEditable } from '@storyblok/react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';

import Container from '@/components/elements/Container';
import Section from '@/components/elements/Section';
import StandardText, {
  StandardTextSize,
} from '@/components/elements/StandardText';

export interface TwoColumnSwapProps {
  blok: any;
}

function SwapSection({ b, i }) {
  const [offsetTop, setOffsetTop] = useState(0);
  const range = 1;
  const { scrollY } = useScroll();
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const onResize = () => {
        const rect = ref.current.getBoundingClientRect();
        setOffsetTop(rect.top);
      };

      onResize();
      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('resize', onResize);
      };
    }

    return () => {};
  }, [ref]);

  const y = useSpring(
    useTransform(
      scrollY,
      [offsetTop - 100, offsetTop + 100],
      ['0%', `${range * 100}%`]
    ),
    { damping: 50, stiffness: 100 }
  );

  return (
    <Container
      size={'wide'}
      className="perspective-normal grid-cols-2 pb-24 md:pb-0 place-content-center lg:grid only-of-type:-mb-12 md:last-of-type:-mb-12 md:first-of-type:-my-12 my-0 md:my-24"
    >
      <div
        className={`pb-12 pt-0 mb-0 lg:py-24 order-${i % 2 === 0 ? 2 : 1} ${
          i % 2 === 0 ? 'lg:pl-24' : 'lg:pr-24'
        }`}
      >
        <StandardText blok={b} size={StandardTextSize.MD} />
      </div>
      <div
        className={`sticky top-12 ${i % 2 === 0 ? 'order-1' : 'order-2'}`}
        ref={ref}
      >
        <div className="aspect-square">
          <svg
            className={`absolute top-0 text-theme-black/20 md:max-h-[800px] ${
              i % 2 === 0 ? 'right-0' : 'left-0'
            } h-full`}
            viewBox="0 0 2128 1103"
            fill="none"
          >
            <path
              d="M994.224 564.645C871.732 389.348 749.038 214.186 626.038 39.2605C606.92 12.0989 580.595 -0.83848 546.047 0.0420721C487.846 1.5661 429.544 0.346878 371.31 0.482348C314.023 0.58395 295.041 14.5373 278.359 70.3169C218.839 269.423 159.657 468.665 100.238 667.805C75.1647 751.864 49.8543 835.855 24.6116 919.846C15.2048 951.106 2.24502 982.061 0.180934 1014.95C-0.56349 1027.07 0.89152 1039.57 6.37319 1050.4C15.9153 1069.24 36.387 1083.32 55.4037 1091.25C76.1799 1099.89 98.9186 1102.43 121.251 1102.39C343.428 1102.09 565.571 1102.39 787.748 1102.15C838.504 1102.09 864.187 1085.86 879.583 1038.59C923.098 905.114 964.345 770.931 1007.29 637.257C1016.08 609.858 1009.42 586.422 994.19 564.611"
              fill="currentColor"
            />
            <path
              d="M1686.64 1101C1576.8 1101 1467 1101.17 1357.16 1100.93C1297.04 1100.8 1274.33 1084.51 1255.89 1027.24C1215.66 902.268 1176.2 777.06 1135.94 652.09C1124.73 617.308 1129.1 587.132 1150.82 556.482C1269.9 388.398 1387.96 219.637 1505.68 50.6049C1529.26 16.7375 1557.89 -1.41536 1600.76 0.447342C1654.9 2.78419 1709.21 0.921486 1763.42 1.02309C1823.68 1.15856 1843.04 15.0103 1860.12 72.3139C1925.02 290.217 1989.72 508.221 2054.22 726.259C2075.7 798.939 2097.22 871.618 2116.98 944.771C2123.62 969.258 2127.03 995.166 2127.91 1020.57C2129.3 1060.29 2114.04 1081.63 2076.07 1092.16C2054.22 1098.22 2030.7 1100.46 2007.89 1100.63C1900.8 1101.48 1793.67 1101 1686.57 1101"
              fill="currentColor"
            />
          </svg>
          <div
            className={`absolute top-0 md:max-h-[800px] ${
              i % 2 === 0 ? 'right-0' : 'left-0'
            } aspect-[1000/1090.17] h-full ${
              i % 2 === 0 ? `[clip-path:url(#right)]` : `[clip-path:url(#left)]`
            }`}
          >
            <motion.div
              initial={{ y: 0 }}
              style={{ y }}
              className="absolute inset-0 "
            >
              <Image
                alt={b.image?.alt || ''}
                src={b.image?.filename}
                fill
                sizes="75vw"
                className="aspect-square object-cover scale-125 -translate-y-20 md:-translate-x-0"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </Container>
  );
}

const TwoColumnSwap: FC<TwoColumnSwapProps> = ({ blok }) => {
  return (
    <Section
      {...storyblokEditable(blok)}
      className="bg-radial-gradient bg-cover overflow-hidden"
      padTop="xl"
      padBottom="xl"
    >
      <svg className="scale-0" style={{ width: 0, height: 0 }}>
        <clipPath id={`right`} clipPathUnits="objectBoundingBox">
          <path d="M0.563,1 C0.453,1,0.343,1,0.233,1 C0.173,1,0.15,0.985,0.131,0.933 C0.091,0.819,0.052,0.705,0.011,0.592 C0,0.56,0.004,0.533,0.026,0.505 C0.145,0.352,0.264,0.199,0.382,0.046 C0.405,0.015,0.434,-0.002,0.477,0 C0.531,0.002,0.586,0,0.64,0.001 C0.7,0.001,0.72,0.013,0.737,0.065 C0.802,0.263,0.867,0.461,0.931,0.659 C0.953,0.725,0.974,0.791,0.994,0.858 C1,0.88,1,0.904,1,0.927 C1,0.963,0.991,0.982,0.953,0.992 C0.931,0.997,0.908,0.999,0.885,0.999 C0.778,1,0.67,1,0.563,1" />
        </clipPath>
        <clipPath id={`left`} clipPathUnits="objectBoundingBox">
          <path d="M0.983,0.512 C0.862,0.353,0.741,0.194,0.619,0.036 C0.6,0.011,0.574,-0.001,0.54,0 C0.482,0.001,0.425,0,0.367,0 C0.31,0,0.292,0.013,0.275,0.064 C0.216,0.244,0.158,0.425,0.099,0.606 C0.074,0.682,0.049,0.758,0.024,0.834 C0.015,0.863,0.002,0.891,0,0.921 C-0.001,0.932,0.001,0.943,0.006,0.953 C0.016,0.97,0.036,0.983,0.055,0.99 C0.075,0.998,0.098,1,0.12,1 C0.339,1,0.559,1,0.779,1 C0.829,1,0.854,0.985,0.87,0.942 C0.913,0.821,0.953,0.699,0.996,0.578 C1,0.553,0.998,0.532,0.983,0.512" />
        </clipPath>
      </svg>
      <div>
        {blok.content.map((b, i) => (
          <SwapSection key={`blok${b._uid}_${i}`} b={b} i={i} />
        ))}
      </div>
    </Section>
  );
};

export default TwoColumnSwap;
